// GEDCOM (5.5, 5.5.1, 7.0) → Tree.
//
// GEDCOM is couple-centric: a FAM record holds up to one HUSB and one WIFE plus children.
// We flatten each FAM into pairwise links (partner + parent→child) so the app never has
// to care about HUSB/WIFE. Religious ordinance records are kept verbatim in passthrough
// (so nothing is lost on re-export) but are not surfaced in the UI.

import { mediaKindOf, mimeFromName } from '../model/files';
import { GENDER_LABELS, markDeceasedFromDates, newId } from '../model/people';
import { placeKey } from '../model/places';
import { newTree } from '../model/tree';
import type {
  Citation, CitationQuality, EventType, Gender, GenderIdentity, Group, GroupKind, Id, LifeEvent, MediaItem, ParentType, PartnerRel, Person, PersonName,
  PlaceLocation, RawRecord, Region, Relationship, SiblingType, SocialRel, Source, Todo, TodoPriority, TodoStatus, Tree,
} from '../model/types';
import { child, childValue, childrenOf, decodeGedcomBytes, parseGedcomText } from './gedcom-lines';

export interface ImportSummary {
  people: number;
  partnerships: number;
  parentLinks: number;
  socialLinks: number;
  groups: number;
  preservedRecords: number;
  hiddenReligiousRecords: number;
  encoding?: string;
  version?: string;
  source?: string;
  /** Profile photos brought in (MacFamilyTree packages). */
  photos?: number;
  sources?: number;
  citations?: number;
  media?: number;
  todos?: number;
  /**
   * Couples with no marriage, divorce, or engagement on record (and no type of ours). They come
   * in as partners; the import window offers to change that.
   */
  unlabeledCouples?: Id[];
  /** Kinds of records in the file that weren't carried over, such as "3 sources". */
  notImported?: string[];
  warnings: string[];
}

export interface ImportResult {
  tree: Tree;
  summary: ImportSummary;
}

export const EVENT_TAGS: Record<string, EventType> = {
  BIRT: 'birth', DEAT: 'death', BURI: 'burial', CREM: 'cremation', RESI: 'residence', EDUC: 'education',
  GRAD: 'graduation', OCCU: 'occupation', IMMI: 'immigration', EMIG: 'emigration', NATU: 'naturalization',
  CENS: 'census', RETI: 'retirement', ADOP: 'adoption', _MILT: 'military', _MILI: 'military', MILI: 'military',
  _MILITARY_SERVICE: 'military',
};

/** Labels we write in EVEN/TYPE for our own event types, matched case-insensitively on import. */
export const EVEN_TYPE_LABELS: Partial<Record<EventType, string>> = {
  military: 'Military service',
  'name-change': 'Name change',
  'gender-transition': 'Transition',
  'coming-out': 'Coming out',
  'hrt-start': 'Started HRT',
};

// Religious and church-ordinance tags. Preserved, never shown.
export const RELIGIOUS_TAGS = new Set([
  'BAPM', 'CHR', 'CHRA', 'CONF', 'FCOM', 'ORDN', 'BARM', 'BASM', 'BLES', 'RELI', 'BAPL', 'CONL', 'ENDL',
  'SLGC', 'SLGS', 'INIL', '_BAPT', '_CONF', '_FUN', 'ORDI',
]);

const MARRIAGE_TAGS = new Set(['MARR', 'MARB', 'MARC', 'MARL', 'MARS']);
const DIVORCE_TAGS = new Set(['DIV', 'DIVF', 'ANUL']);

const REL_TO_PARENT_TYPE: [RegExp, ParentType][] = [
  [/^(birth|natural|biological|bio)$/i, 'biological'],
  [/adopt/i, 'adoptive'],
  [/step/i, 'step'],
  [/foster/i, 'foster'],
  [/guardian/i, 'guardian'],
  [/chosen/i, 'chosen'],
  [/surrogat/i, 'surrogate'],
  [/donor/i, 'donor'],
  [/seal|private|unknown|other/i, 'unknown'],
];

export function parentTypeFromText(v: string | undefined): ParentType | undefined {
  if (!v) return undefined;
  for (const [re, t] of REL_TO_PARENT_TYPE) if (re.test(v.trim())) return t;
  return undefined;
}

type SocialMapping =
  | { kind: 'social'; type: SocialRel['type']; reverse?: boolean }
  | { kind: 'sibling'; type: SiblingType }
  | { kind: 'parent'; type: ParentType; childIsOwner: boolean };

const ASSO_TEXT: [RegExp, SocialMapping][] = [
  [/^best\s*friend/i, { kind: 'social', type: 'best-friend' }],
  [/^close\s*friend/i, { kind: 'social', type: 'close-friend' }],
  [/^family\s*friend/i, { kind: 'social', type: 'family-friend' }],
  [/^chosen\s*(sibling|sister|brother)/i, { kind: 'sibling', type: 'chosen' }],
  [/^(half[-\s]?)(sibling|sister|brother)/i, { kind: 'sibling', type: 'half' }],
  [/^step[-\s]?(sibling|sister|brother)/i, { kind: 'sibling', type: 'step' }],
  [/^adoptive\s*(sibling|sister|brother)/i, { kind: 'sibling', type: 'adoptive' }],
  [/^foster\s*(sibling|sister|brother)/i, { kind: 'sibling', type: 'foster' }],
  // Chosen family between peers is a chosen sibling; chosen parents come in as parent links.
  [/^chosen\s*(family|kin)/i, { kind: 'sibling', type: 'chosen' }],
  [/^(friend|frnd)s?$/i, { kind: 'social', type: 'friend' }],
  [/^mentor/i, { kind: 'social', type: 'mentor' }],
  [/^(mentee|prot[eé]g[eé])/i, { kind: 'social', type: 'mentor', reverse: true }],
  [/^(room|flat|house)\s*mate/i, { kind: 'social', type: 'roommate' }],
  [/^(colleague|co-?worker)/i, { kind: 'social', type: 'colleague' }],
  [/^(neighbou?r|nghbr)/i, { kind: 'social', type: 'neighbor' }],
];

function parseName(r: RawRecord): PersonName {
  const raw = r.value ?? '';
  const m = raw.match(/^([^/]*)(?:\/([^/]*)\/?)?(.*)$/);
  let given = (m?.[1] ?? raw).trim();
  let surname = (m?.[2] ?? '').trim();
  let suffix = (m?.[3] ?? '').trim();
  const givn = childValue(r, 'GIVN');
  const surn = childValue(r, 'SURN');
  const spfx = childValue(r, 'SPFX');
  const npfx = childValue(r, 'NPFX');
  const nsfx = childValue(r, 'NSFX');
  const nick = childValue(r, 'NICK');
  if (givn) given = givn;
  if (surn) surname = spfx ? `${spfx} ${surn}` : surn;
  if (nsfx) suffix = nsfx;
  // Prefix often also appears at the front of the given part ("Dr. Jane /Doe/"); strip duplicates.
  if (npfx && given.startsWith(npfx + ' ')) given = given.slice(npfx.length + 1);
  const typeRaw = (childValue(r, 'TYPE') ?? '').toLowerCase();
  const phrase = (childValue(child(r, 'TYPE') ?? { tag: '', children: [] }, 'PHRASE') ?? '').toLowerCase();
  // A NAME without a TYPE is just a name; only an explicit birth/maiden type means "birth name".
  let type: PersonName['type'] = 'name';
  const t = phrase || typeRaw;
  if (!t) type = 'name';
  else if (/dead/.test(t)) type = 'deadname';
  else if (/chosen|preferred/.test(t)) type = 'chosen';
  else if (/married/.test(t)) type = 'married';
  else if (/aka|also|alias|professional|stage|nick/.test(t)) type = 'alias';
  else if (/birth|maiden/.test(t)) type = 'birth';
  else type = 'other';
  const name: PersonName = { given, surname, type };
  if (npfx) name.prefix = npfx;
  if (suffix) name.suffix = suffix;
  if (nick) name.nickname = nick;
  if (childValue(r, '_HIDDEN') === 'Y') name.hidden = true;
  return name;
}

function genderFrom(indi: RawRecord): Gender {
  const custom = childValue(indi, '_GENDER')?.trim();
  if (custom) {
    const lc = custom.toLowerCase();
    if (lc === 'woman' || lc === 'female') return { identity: 'woman' };
    if (lc === 'man' || lc === 'male') return { identity: 'man' };
    if (lc === 'nonbinary' || lc === 'non-binary' || lc === 'enby') return { identity: 'nonbinary' };
    if (lc === 'unknown') return { identity: 'unknown' };
    const known = (Object.keys(GENDER_LABELS) as GenderIdentity[]).find((k) => k === lc.replace(/\s+/g, '-') || GENDER_LABELS[k].toLowerCase() === lc);
    if (known && known !== 'custom') return { identity: known };
    return { identity: 'custom', label: custom };
  }
  switch ((childValue(indi, 'SEX') ?? '').trim().toUpperCase()[0]) {
    case 'F': return { identity: 'woman' };
    case 'M': return { identity: 'man' };
    case 'X':
    case 'N': return { identity: 'nonbinary' };
    default: return { identity: 'unknown' };
  }
}

/** GEDCOM 5.5.1 FORM values are file extensions; 7.0 uses MIME types. */
function mimeOfForm(form: string | undefined, path: string): string {
  const f = (form ?? '').trim().toLowerCase();
  if (f.includes('/')) return f;
  if (f) return mimeFromName(`x.${f}`);
  return mimeFromName(path);
}

/** "_REGION 0.1 0.2 0.3 0.4": our extension for where someone is in a picture. */
function regionFrom(text: string | undefined): Region | undefined {
  const n = text?.trim().split(/\s+/).map(Number);
  if (!n || n.length !== 4 || n.some((x) => !Number.isFinite(x)) || n[2] <= 0 || n[3] <= 0) return undefined;
  return { x: n[0], y: n[1], w: n[2], h: n[3] };
}

const TODO_STATUSES: TodoStatus[] = ['open', 'in-progress', 'waiting', 'done'];
const TODO_PRIORITIES: TodoPriority[] = ['low', 'normal', 'high'];

export function importGedcomBytes(bytes: Uint8Array, fileName?: string): ImportResult {
  const { text, encoding } = decodeGedcomBytes(bytes);
  const res = importGedcom(text, fileName);
  res.summary.encoding = encoding;
  return res;
}

export function importGedcom(text: string, fileName?: string): ImportResult {
  const parsed = parseGedcomText(text);
  const warnings = [...parsed.warnings];
  const records = parsed.records;
  const head = records.find((r) => r.tag === 'HEAD');
  const version = head ? childValue(child(head, 'GEDC') ?? head, 'VERS') : undefined;
  const source = head ? childValue(child(head, 'SOUR') ?? head, 'NAME') ?? childValue(head, 'SOUR') : undefined;
  const treeName = (head && childValue(head, '_TREE')) || fileName?.replace(/\.(ged|gedcom)$/i, '') || 'Imported tree';

  if (!records.length || !head) warnings.unshift("This file doesn't look like GEDCOM (no HEAD record). Imported what we could.");

  const tree = newTree(treeName);
  const people: Record<Id, Person> = {};
  const relationships: Record<Id, Relationship> = {};
  const groups: Record<Id, Group> = {};
  const passthrough: RawRecord[] = [];
  const xrefToId = new Map<string, Id>();
  const noteRecords = new Map<string, string>();
  let hiddenReligious = 0;

  for (const r of records) if (r.tag === 'NOTE' && r.xref) noteRecords.set(r.xref, r.value ?? '');
  for (const r of records) if (r.tag === 'SNOTE' && r.xref) noteRecords.set(r.xref, r.value ?? '');

  const noteText = (r: RawRecord): string => {
    const v = r.value ?? '';
    if (/^@[^@]+@$/.test(v)) return noteRecords.get(v) ?? '';
    return v;
  };

  const addRel = (r: Relationship) => {
    relationships[r.id] = r;
  };

  // ---- Sources, media, and to-dos come first so people and families can point at them.
  const repoNames = new Map<string, string>();
  for (const r of records) if (r.tag === 'REPO' && r.xref) repoNames.set(r.xref, childValue(r, 'NAME') ?? '');
  const sources: Record<Id, Source> = {};
  const sourceByXref = new Map<string, Id>();
  const sourceByText = new Map<string, Id>();
  for (const r of records) if (r.tag === 'SOUR' && r.xref) sourceByXref.set(r.xref, newId('s'));
  const media: Record<Id, MediaItem> = {};
  const mediaByXref = new Map<string, MediaItem>();
  const todos: Record<Id, Todo> = {};
  const todoByXref = new Map<string, Todo>();
  let citationCount = 0;

  /** A citation from a SOUR structure: a pointer with PAGE/QUAY, or (GEDCOM 5.5) inline text. */
  const citationFrom = (c: RawRecord): Citation | undefined => {
    const v = (c.value ?? '').trim();
    let sid = sourceByXref.get(v);
    if (!sid) {
      // An inline source ("2 SOUR Parish register of St. Mary's") becomes a source of its own.
      const text = [v, ...childrenOf(c, 'TEXT').map((t) => t.value ?? '')].filter(Boolean).join('\n').trim();
      if (!text || /^@[^@]+@$/.test(v)) return undefined;
      sid = sourceByText.get(text);
      if (!sid) {
        sid = newId('s');
        sourceByText.set(text, sid);
        const firstLine = text.split('\n')[0];
        sources[sid] = { id: sid, title: firstLine.length > 120 ? firstLine.slice(0, 117) + '…' : firstLine, ...(text !== firstLine || firstLine.length > 120 ? { text } : {}) };
      }
    }
    const out: Citation = { sourceId: sid };
    const page = childValue(c, 'PAGE');
    if (page) out.page = page;
    const quay = Number(childValue(c, 'QUAY'));
    if ([0, 1, 2, 3].includes(quay) && childValue(c, 'QUAY') !== undefined) out.quality = quay as CitationQuality;
    const data = child(c, 'DATA');
    const note = [
      ...childrenOf(c, 'NOTE').map(noteText),
      ...(data ? childrenOf(data, 'TEXT').map((t) => t.value ?? '') : []),
    ].filter(Boolean).join('\n');
    if (note) out.note = note;
    citationCount++;
    return out;
  };

  // Media and to-dos exported by this app keep their ids (_RID), as people do, so adding the
  // file back to its tree finds them instead of adding them again.
  const usedRids = new Set<Id>();
  const ownId = (r: RawRecord, prefix: string): Id => {
    const rid = childValue(r, '_RID');
    if (rid && /^[\w-]{1,64}$/.test(rid) && !usedRids.has(rid)) {
      usedRids.add(rid);
      return rid;
    }
    return newId(prefix);
  };
  const mediaFrom = (r: RawRecord): MediaItem => {
    const file = child(r, 'FILE');
    const path = (file?.value ?? '').trim();
    const form = (file && childValue(file, 'FORM')) ?? childValue(r, 'FORM');
    const type = mimeOfForm(form, path);
    const remote = /^https?:\/\//i.test(path);
    let kind = mediaKindOf(type, path);
    if (remote && kind === 'file') kind = 'link';
    const title = (file && childValue(file, 'TITL')) ?? childValue(r, 'TITL') ?? path.split(/[\\/]/).pop()?.replace(/\.[^.]+$/, '') ?? '';
    const item: MediaItem = { id: ownId(r, 'm'), title: title || 'Untitled', kind, people: [] };
    // The file itself isn't in a .ged; keep where it was so it can be found and added later.
    if (path) item.url = path;
    const date = childValue(r, '_DATE') ?? childValue(r, 'DATE');
    if (date) item.date = date;
    const notes = childrenOf(r, 'NOTE').map(noteText).filter(Boolean);
    if (notes.length) item.description = notes.join('\n\n');
    const srcs = childrenOf(r, 'SOUR').map((c) => sourceByXref.get(c.value ?? '')).filter((x): x is Id => !!x);
    if (srcs.length) item.sourceIds = srcs;
    if (r.xref) item.gedcomId = r.xref;
    media[item.id] = item;
    return item;
  };
  for (const r of records) if (r.tag === 'OBJE' && r.xref) mediaByXref.set(r.xref, mediaFrom(r));

  /** A link's details from our extension tags under an ASSO or _FREL/_MREL (see linkDetails in the exporter). */
  const linkDetailsFrom = (r: RawRecord): Partial<Pick<SocialRel, 'label' | 'start' | 'end' | 'place' | 'notes' | 'citations'>> => {
    const out: Partial<Pick<SocialRel, 'label' | 'start' | 'end' | 'place' | 'notes' | 'citations'>> = {};
    const label = childValue(r, '_LABEL');
    if (label) out.label = label;
    const start = childValue(r, '_DATE') ?? childValue(r, 'DATE');
    if (start) out.start = start;
    const end = childValue(r, '_END');
    if (end) out.end = end;
    const place = childValue(r, '_PLACE');
    if (place) out.place = place;
    const notes = childrenOf(r, 'NOTE').map(noteText).filter(Boolean).join('\n\n');
    if (notes) out.notes = notes;
    const cites = childrenOf(r, 'SOUR').map(citationFrom).filter((c): c is Citation => !!c);
    if (cites.length) out.citations = cites;
    return out;
  };

  /** An OBJE under a person, family, or event: a pointer to a record, or an inline object. */
  const linkMedia = (c: RawRecord, ids: Id[]) => {
    const item = (c.value && mediaByXref.get(c.value)) || (!c.value || !/^@[^@]+@$/.test(c.value) ? mediaFrom(c) : undefined);
    if (!item) return;
    const region = regionFrom(childValue(c, '_REGION'));
    for (const id of ids) if (!item.people.some((x) => x.personId === id)) item.people.push(region ? { personId: id, region } : { personId: id });
  };

  const todoFrom = (r: RawRecord): Todo => {
    const statText = (childValue(r, '_STAT') ?? childValue(r, 'STAT') ?? '').toLowerCase().replace(/\s+/g, '-');
    const status: TodoStatus = TODO_STATUSES.find((x) => x === statText) ?? (/done|complete|closed/.test(statText) ? 'done' : 'open');
    const prio = (childValue(r, '_PRIO') ?? childValue(r, 'PRIO') ?? childValue(r, '_PRIORITY') ?? '').toLowerCase();
    const title = childValue(r, 'TITL') ?? childValue(r, 'DESC') ?? (r.value && !/^@/.test(r.value) ? r.value : undefined) ?? childValue(r, 'TEXT') ?? 'Untitled to-do';
    const td: Todo = {
      id: ownId(r, 'td'),
      title,
      status,
      priority: TODO_PRIORITIES.find((x) => x === prio) ?? 'normal',
      people: [],
      createdAt: (() => {
        const made = childValue(r, '_CREATED');
        return made && !isNaN(Date.parse(made)) ? made : new Date().toISOString();
      })(),
    };
    const kind = childValue(r, 'TYPE');
    if (kind) td.kind = kind;
    const due = childValue(r, '_DUE');
    if (due && /^\d{4}-\d{2}-\d{2}$/.test(due)) td.due = due;
    const done = childValue(r, '_DONE');
    if (done) td.doneAt = done;
    else if (status === 'done') td.doneAt = td.createdAt;
    const notes = childrenOf(r, 'NOTE').map(noteText).filter((n) => n && n !== title);
    if (notes.length) td.notes = notes.join('\n\n');
    todos[td.id] = td;
    return td;
  };
  for (const r of records) if (r.tag === '_TODO' && r.xref) todoByXref.set(r.xref, todoFrom(r));

  for (const r of records) {
    if (r.tag !== 'SOUR' || !r.xref) continue;
    const id = sourceByXref.get(r.xref)!;
    const src: Source = { id, title: childValue(r, 'TITL') ?? childValue(r, 'ABBR') ?? 'Untitled source', gedcomId: r.xref };
    const extra: RawRecord[] = [];
    const notes: string[] = [];
    for (const c of r.children) {
      if (c.tag === 'TITL') continue;
      else if (c.tag === 'AUTH' && c.value) src.author = c.value;
      else if (c.tag === 'PUBL' && c.value) src.publication = c.value;
      else if (c.tag === 'TEXT' && c.value) src.text = c.value;
      else if (c.tag === 'ABBR' && c.value) {
        if (src.title !== c.value) notes.push(`Abbreviation: ${c.value}`);
      } else if (c.tag === 'NOTE') notes.push(noteText(c));
      else if ((c.tag === '_TYPE' || c.tag === 'TYPE' || c.tag === '_MEDI') && c.value) src.kind = c.value;
      else if ((c.tag === '_URL' || c.tag === 'WWW' || c.tag === '_LINK' || c.tag === 'URL') && c.value) src.url = c.value;
      else if (c.tag === 'REPO') {
        const name = c.value ? repoNames.get(c.value) : childValue(c, 'NAME');
        if (name) src.repository = name;
        if (c.value && repoNames.has(c.value)) src.repositoryXref = c.value;
        const caln = childValue(c, 'CALN');
        if (caln) src.callNumber = caln;
        const medi = childValue(child(c, 'CALN') ?? c, 'MEDI');
        if (medi && !src.kind) src.kind = medi;
      } else if (c.tag === 'DATA') {
        const ev = child(c, 'EVEN');
        const date = childValue(ev ?? c, 'DATE') ?? childValue(c, 'DATE');
        const place = ev ? childValue(ev, 'PLAC') : undefined;
        if (date) src.date = date.trim();
        if (place) src.place = place;
        const agnc = childValue(c, 'AGNC');
        if (agnc) notes.push(`Agency: ${agnc}`);
        notes.push(...childrenOf(c, 'NOTE').map(noteText));
      } else if (c.tag === 'OBJE') {
        const m = (c.value && mediaByXref.get(c.value)) || (!c.value ? mediaFrom(c) : undefined);
        if (m && !m.sourceIds?.includes(id)) m.sourceIds = [...(m.sourceIds ?? []), id];
      } else if (c.tag === 'CHAN' || c.tag === 'RIN') continue;
      else extra.push(c);
    }
    const n = notes.filter(Boolean).join('\n\n');
    if (n) src.notes = n;
    if (extra.length) src.passthrough = extra;
    sources[id] = src;
  }

  // ---- INDI pass 1: create people so pointers resolve in any order.
  const indis = records.filter((r) => r.tag === 'INDI');
  // People exported by this app keep their ids (_RID), so adding the file back to the tree it
  // came from combines each person with themselves instead of adding them again.
  const takenIds = new Set<Id>();
  for (const r of indis) {
    if (!r.xref) continue;
    const rid = childValue(r, '_RID');
    const id = rid && /^[\w-]{1,64}$/.test(rid) && !takenIds.has(rid) ? rid : newId('p');
    takenIds.add(id);
    xrefToId.set(r.xref, id);
  }

  const pendingAsso: { from: Id; rec: RawRecord }[] = [];
  // child id → fam xref → per-parent-slot types from the child's FAMC / ADOP
  const famcInfo = new Map<Id, Map<string, { pedi?: ParentType; adopBy?: 'HUSB' | 'WIFE' | 'BOTH' }>>();

  for (const r of indis) {
    if (!r.xref) continue;
    const id = xrefToId.get(r.xref)!;
    const person: Person = {
      id,
      names: [],
      gender: genderFrom(r),
      living: null,
      events: [],
      notes: '',
      tags: [],
      gedcomId: r.xref,
    };
    const extra: RawRecord[] = [];
    const notes: string[] = [];
    for (const c of r.children) {
      const tag = c.tag;
      if (tag === 'NAME') person.names.push(parseName(c));
      else if (tag === 'SEX' || tag === '_GENDER' || tag === '_RID') continue;
      else if (tag === '_PRONOUNS') person.pronouns = c.value;
      else if (tag === '_TERMS' && (c.value === 'feminine' || c.value === 'masculine' || c.value === 'neutral')) person.terms = c.value;
      else if (tag === '_LIVING') person.living = c.value === 'Y' ? true : c.value === 'N' ? false : null;
      else if (tag === '_TAG' && c.value) person.tags.push(c.value);
      else if (tag === 'NOTE') notes.push(noteText(c));
      else if (tag === 'FAMS') continue;
      else if (tag === 'FAMC' && c.value) {
        const info = famcInfo.get(id) ?? new Map();
        const cur = info.get(c.value) ?? {};
        const pedi = parentTypeFromText(childValue(c, 'PEDI'));
        if (pedi) cur.pedi = pedi;
        info.set(c.value, cur);
        famcInfo.set(id, info);
      } else if (tag === 'ASSO') pendingAsso.push({ from: id, rec: c });
      else if (tag === 'SOUR') {
        const cite = citationFrom(c);
        if (cite) (person.citations ??= []).push(cite);
        else extra.push(c);
      } else if (tag === 'OBJE') linkMedia(c, [id]);
      else if (tag === '_TODO') {
        const td = (c.value && todoByXref.get(c.value)) || (!c.value || !/^@[^@]+@$/.test(c.value) ? todoFrom(c) : undefined);
        if (td && !td.people.includes(id)) td.people.push(id);
      }
      else if (RELIGIOUS_TAGS.has(tag)) {
        hiddenReligious++;
        extra.push(c);
      } else if (EVENT_TAGS[tag] || tag === 'EVEN' || tag === 'FACT') {
        const ev = eventFrom(c, noteText, citationFrom, (o) => linkMedia(o, [id]));
        if (ev) person.events.push(ev);
        if (tag === 'ADOP') {
          const famc = child(c, 'FAMC');
          if (famc?.value) {
            const info = famcInfo.get(id) ?? new Map();
            const cur = info.get(famc.value) ?? {};
            const by = (childValue(famc, 'ADOP') ?? 'BOTH').toUpperCase();
            cur.adopBy = by === 'HUSB' || by === 'WIFE' ? by : 'BOTH';
            info.set(famc.value, cur);
            famcInfo.set(id, info);
          }
        }
      } else extra.push(c);
    }
    if (!person.names.length) person.names.push({ given: '', surname: '', type: 'name' });
    person.notes = notes.filter(Boolean).join('\n\n');
    if (extra.length) person.passthrough = extra;
    people[id] = person;
  }

  // ---- FAM records
  let partnerships = 0;
  const unlabeledCouples: Id[] = [];
  let parentLinks = 0;
  for (const f of records.filter((r) => r.tag === 'FAM')) {
    const husb = childrenOf(f, 'HUSB').map((c) => xrefToId.get(c.value ?? '')).filter((x): x is Id => !!x);
    const wife = childrenOf(f, 'WIFE').map((c) => xrefToId.get(c.value ?? '')).filter((x): x is Id => !!x);
    const parents = [...husb.map((id) => ({ id, slot: 'HUSB' as const })), ...wife.map((id) => ({ id, slot: 'WIFE' as const }))];
    const chil = childrenOf(f, 'CHIL');
    for (const c of [...childrenOf(f, 'HUSB'), ...childrenOf(f, 'WIFE'), ...chil])
      if (c.value && !xrefToId.has(c.value)) warnings.push(`Family ${f.xref ?? ''} points to a missing person ${c.value}.`);

    // Partnership between the (up to two) parents.
    if (parents.length === 2) {
      const consumed = new Set(['HUSB', 'WIFE', 'CHIL', 'NOTE', '_PTYPE', '_STAT', '_SEPR', '_SEPARATED', 'NCHI', 'RIN', 'SOUR', 'OBJE', '_LABEL', '_DATE', '_END', '_PLACE']);
      // Prefer a real MARR over banns/contract/license records.
      const marr = child(f, 'MARR') ?? f.children.find((c) => MARRIAGE_TAGS.has(c.tag));
      const div = child(f, 'DIV') ?? f.children.find((c) => DIVORCE_TAGS.has(c.tag));
      const enga = marr ? undefined : child(f, 'ENGA');
      if (enga) consumed.add('ENGA');
      const sep = child(f, '_SEPR') ?? child(f, '_SEPARATED');
      let type: PartnerRel['type'] = marr || div ? 'married' : enga ? 'engaged' : 'partner';
      const marrType = marr ? (childValue(marr, 'TYPE') ?? '').toLowerCase() : '';
      if (/civil/.test(marrType)) type = 'civil-union';
      else if (/domestic/.test(marrType)) type = 'domestic';
      const ptype = childValue(f, '_PTYPE') as PartnerRel['type'] | undefined;
      if (ptype && ['married', 'partner', 'civil-union', 'domestic', 'engaged', 'dating', 'other'].includes(ptype)) type = ptype;
      // Nothing says what kind of couple they are; the import window asks.
      const unlabeled = !marr && !div && !enga && !ptype;
      let status: PartnerRel['status'] = div ? 'divorced' : sep ? 'separated' : 'current';
      const stat = childValue(f, '_STAT') as PartnerRel['status'] | undefined;
      if (stat && ['current', 'separated', 'divorced', 'widowed', 'ended'].includes(stat)) status = stat;
      const rel: PartnerRel = {
        id: newId('r'),
        kind: 'partner',
        a: parents[0].id,
        b: parents[1].id,
        type,
        status,
      };
      const start = marr ? childValue(marr, 'DATE') : enga ? childValue(enga, 'DATE') : undefined;
      if (start) rel.start = start;
      const end = div ? childValue(div, 'DATE') : undefined;
      if (end) rel.end = end;
      const place = marr ? childValue(marr, 'PLAC') : undefined;
      if (place) rel.place = place;
      // Our own tags carry what MARR/ENGA/DIV couldn't: a label, a start for couples who aren't
      // married, an end that isn't a divorce.
      const label = childValue(f, '_LABEL');
      if (label) rel.label = label;
      rel.start ??= childValue(f, '_DATE');
      rel.end ??= childValue(f, '_END');
      rel.place ??= childValue(f, '_PLACE');
      for (const k of ['start', 'end', 'place'] as const) if (!rel[k]) delete rel[k];
      const notes = childrenOf(f, 'NOTE').map(noteText).filter(Boolean);
      if (notes.length) rel.notes = notes.join('\n\n');
      // Keep extra detail on the marriage/divorce (sources etc.) under wrapper tags that the
      // exporter re-attaches beneath the MARR/DIV it writes, plus any other FAM sub-records.
      const extra: RawRecord[] = [];
      const cites: Citation[] = [];
      const pair = [parents[0].id, parents[1].id];
      for (const c of [...childrenOf(f, 'SOUR'), ...(marr ? childrenOf(marr, 'SOUR') : []), ...(div ? childrenOf(div, 'SOUR') : [])]) {
        const cite = citationFrom(c);
        if (cite) cites.push(cite);
      }
      for (const c of [...childrenOf(f, 'OBJE'), ...(marr ? childrenOf(marr, 'OBJE') : []), ...(div ? childrenOf(div, 'OBJE') : [])]) linkMedia(c, pair);
      if (cites.length) rel.citations = cites;
      for (const c of f.children) {
        if ((c === marr && c.tag === 'MARR') || (c === div && c.tag === 'DIV')) {
          const sub = c.children.filter((x) => !['DATE', 'PLAC', 'TYPE', 'SOUR', 'OBJE'].includes(x.tag));
          if (sub.length) extra.push({ tag: c.tag === 'MARR' ? '__MARR' : '__DIV', children: sub });
          continue;
        }
        if (!consumed.has(c.tag)) extra.push(c);
      }
      if (extra.length) rel.passthrough = extra;
      addRel(rel);
      if (unlabeled) unlabeledCouples.push(rel.id);
      partnerships++;
    } else if (parents.length < 2) {
      const extra = f.children.filter((c) => !['HUSB', 'WIFE', 'CHIL', 'NCHI', 'RIN'].includes(c.tag));
      if (extra.length && parents.length === 1)
        warnings.push(`Family ${f.xref ?? ''} has details but only one partner; those details were not kept.`);
    }

    // Parent → child links.
    const childIds: Id[] = [];
    for (const c of chil) {
      const cid = xrefToId.get(c.value ?? '');
      if (!cid) continue;
      childIds.push(cid);
      const info = famcInfo.get(cid)?.get(f.xref ?? '');
      for (const p of parents) {
        if (p.id === cid) continue;
        // Priority: Ancestry/FTM-style _FREL/_MREL on CHIL > ADOP event > FAMC PEDI > biological.
        const relTag = p.slot === 'HUSB' ? '_FREL' : '_MREL';
        let type: ParentType =
          parentTypeFromText(childValue(c, relTag)) ??
          (info?.adopBy && (info.adopBy === 'BOTH' || info.adopBy === p.slot) ? 'adoptive' : undefined) ??
          info?.pedi ??
          'biological';
        if (type === 'unknown' && !childValue(c, relTag)) type = 'biological';
        const details = child(c, relTag);
        addRel({ id: newId('r'), kind: 'parent', a: p.id, b: cid, type, ...(details ? linkDetailsFrom(details) : {}) });
        parentLinks++;
      }
    }
    // Siblings in a family with no recorded parents.
    if (!parents.length && childIds.length > 1) {
      for (let i = 0; i < childIds.length; i++)
        for (let j = i + 1; j < childIds.length; j++)
          addRel({ id: newId('r'), kind: 'sibling', a: childIds[i], b: childIds[j], type: 'unknown' });
    }
  }

  // ---- ASSO → social / sibling / parent links, de-duplicated across both directions.
  let socialLinks = 0;
  const seenPairs = new Set<string>();
  for (const { from, rec: a } of pendingAsso) {
    const to = xrefToId.get(a.value ?? '');
    if (!to || to === from) continue;
    const role = child(a, 'ROLE');
    const text = (childValue(a, 'RELA') ?? childValue(role ?? { tag: '', children: [] }, 'PHRASE') ?? role?.value ?? '').trim();
    const ourType = childValue(a, '_TYPE');
    let mapping: SocialMapping | undefined;
    if (ourType) {
      const [kind, type, dir] = ourType.split(':');
      if (kind === 'social') mapping = { kind: 'social', type: type as SocialRel['type'], reverse: dir === 'reverse' };
      else if (kind === 'sibling') mapping = { kind: 'sibling', type: type as SiblingType };
    }
    if (!mapping) {
      if (/^(FRIEND)$/i.test(text)) mapping = { kind: 'social', type: 'friend' };
      else if (/^NGHBR$/i.test(text)) mapping = { kind: 'social', type: 'neighbor' };
      else for (const [re, m] of ASSO_TEXT) if (re.test(text)) { mapping = m; break; }
    }
    mapping ??= { kind: 'social', type: 'other' };
    const key = [mapping.kind, ...[from, to].sort()].join('|');
    if (seenPairs.has(key)) continue;
    seenPairs.add(key);
    const details = linkDetailsFrom(a);
    if (mapping.kind === 'sibling') {
      addRel({ id: newId('r'), kind: 'sibling', a: from, b: to, type: mapping.type, ...details });
    } else if (mapping.kind === 'social') {
      // ASSO: "the associated person (to) is the <RELA> of the owner (from)".
      // Our mentor rel is "a mentors b", so "to is mentor of from" → a = to.
      const mentorFlip = mapping.type === 'mentor' && !mapping.reverse;
      const rel: SocialRel = {
        id: newId('r'),
        kind: 'social',
        a: mentorFlip ? to : from,
        b: mentorFlip ? from : to,
        type: mapping.type,
      };
      if (mapping.type === 'other' && text) rel.label = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
      Object.assign(rel, details);
      addRel(rel);
    }
    socialLinks++;
  }

  // ---- Our own _GROUP records
  for (const g of records.filter((r) => r.tag === '_GROUP')) {
    const kind = (childValue(g, 'TYPE') ?? 'other') as GroupKind;
    const group: Group = {
      id: newId('g'),
      name: childValue(g, 'NAME') ?? 'Group',
      kind: ['friend-group', 'found-family', 'household', 'family', 'organization', 'other'].includes(kind) ? kind : 'other',
      description: childrenOf(g, 'NOTE').map(noteText).join('\n\n'),
      color: childValue(g, '_COLOR') ?? '#7c6cf0',
      members: childrenOf(g, '_MEMB')
        .map((m) => ({ personId: xrefToId.get(m.value ?? '') ?? '', role: childValue(m, 'ROLE') }))
        .filter((m) => m.personId)
        .map((m) => (m.role ? m : { personId: m.personId })),
    };
    groups[group.id] = group;
  }

  // ---- Everything else at the top level is preserved verbatim.
  for (const r of records) {
    if (['HEAD', 'TRLR', 'INDI', 'FAM', '_GROUP'].includes(r.tag)) continue;
    // Sources, media, and to-dos are modeled now; repositories stay preserved for sources to point at.
    if ((r.tag === 'SOUR' || r.tag === 'OBJE' || r.tag === '_TODO') && r.xref) continue;
    passthrough.push(r);
  }

  // Deceased when their records show it; otherwise as the file says, or not recorded.
  markDeceasedFromDates(Object.values(people), Object.values(relationships));

  const homeXref = head ? childValue(head, '_HOME') : undefined;
  const out: Tree = {
    ...tree,
    people,
    relationships,
    groups,
    sources,
    media,
    todos,
    passthrough: passthrough.length ? passthrough : undefined,
    homePersonId: homeXref ? xrefToId.get(homeXref) : undefined,
    locations: placeLocations(records),
  };
  if (!out.homePersonId) {
    const first = indis[0]?.xref;
    if (first) out.homePersonId = xrefToId.get(first);
  }
  return {
    tree: out,
    summary: {
      people: Object.keys(people).length,
      partnerships,
      parentLinks,
      socialLinks,
      groups: Object.keys(groups).length,
      preservedRecords: passthrough.length,
      hiddenReligiousRecords: hiddenReligious,
      sources: Object.keys(sources).length,
      citations: citationCount,
      unlabeledCouples,
      media: Object.keys(media).length,
      todos: Object.keys(todos).length,
      version,
      source,
      warnings,
    },
  };
}

function eventFrom(
  r: RawRecord,
  noteText: (r: RawRecord) => string,
  citationFrom: (r: RawRecord) => Citation | undefined,
  linkMedia: (r: RawRecord) => void,
): LifeEvent | undefined {
  let type: EventType = EVENT_TAGS[r.tag] ?? 'custom';
  const ev: LifeEvent = { id: newId('e'), type };
  const typeText = childValue(r, 'TYPE')?.trim();
  if (r.tag === 'EVEN' || r.tag === 'FACT') {
    const match = (Object.entries(EVEN_TYPE_LABELS) as [EventType, string][]).find(([, l]) => l.toLowerCase() === typeText?.toLowerCase());
    if (match) type = match[0];
    ev.type = type;
    if (type === 'custom') {
      const label = [typeText, r.value && r.value !== 'Y' ? r.value : ''].filter(Boolean).join(': ');
      ev.label = label || 'Event';
    } else if (r.value && r.value !== 'Y') ev.label = r.value;
  } else if (r.value && r.value !== 'Y') {
    ev.label = r.value;
  } else if (typeText && type !== 'birth' && type !== 'death') {
    ev.label = typeText;
  }
  const extra: RawRecord[] = [];
  const notes: string[] = [];
  for (const c of r.children) {
    if (c.tag === 'DATE') {
      const phrase = childValue(c, 'PHRASE');
      if (c.value) ev.date = c.value.trim();
      else if (phrase) ev.date = `(${phrase})`;
    } else if (c.tag === 'PLAC') {
      ev.place = c.value?.trim();
      if (c.children.length) extra.push({ tag: '__PLAC', children: c.children });
    } else if (c.tag === 'NOTE') notes.push(noteText(c));
    else if (c.tag === 'CAUS' && c.value) notes.push(`Cause: ${c.value}`);
    else if (c.tag === 'SOUR') {
      const cite = citationFrom(c);
      if (cite) (ev.citations ??= []).push(cite);
      else extra.push(c);
    } else if (c.tag === 'OBJE') linkMedia(c);
    else if (c.tag === 'TYPE') continue;
    else if (c.tag === 'FAMC' && r.tag === 'ADOP') continue;
    else extra.push(c);
  }
  if (notes.length) ev.note = notes.filter(Boolean).join('\n');
  if (extra.length) ev.passthrough = extra;
  return ev;
}

/**
 * Where places are, from `PLAC` › `MAP` › `LATI`/`LONG` anywhere in the file, keyed by place.
 * The MAP lines also stay in each event's preserved place details, so they're written back as they were.
 */
function placeLocations(records: RawRecord[]): Record<string, PlaceLocation> | undefined {
  const out: Record<string, PlaceLocation> = {};
  const visit = (r: RawRecord) => {
    if (r.tag === 'PLAC' && r.value?.trim()) {
      const map = child(r, 'MAP');
      const lat = map && gedcomDegrees(childValue(map, 'LATI'), 'N', 'S');
      const lon = map && gedcomDegrees(childValue(map, 'LONG'), 'E', 'W');
      if (lat !== undefined && lon !== undefined && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) out[placeKey(r.value)] ??= { lat, lon };
    }
    r.children.forEach(visit);
  };
  records.forEach(visit);
  return Object.keys(out).length ? out : undefined;
}

/** "N42.8864" or "W78.8784" (some programs write "-78.8784" or "78.8784W") as signed degrees. */
export function gedcomDegrees(v: string | undefined, pos: string, neg: string): number | undefined {
  const m = v?.trim().toUpperCase().match(/^([NSEW])?\s*(-?\d+(?:\.\d+)?)\s*([NSEW])?$/);
  if (!m) return undefined;
  const hemi = m[1] ?? m[3];
  if (hemi && hemi !== pos && hemi !== neg) return undefined;
  const n = parseFloat(m[2]);
  return hemi === neg ? -Math.abs(n) : n;
}
